import { DisqualificationReason } from './disqualifications'
import { toCents } from './general'
import { track } from './analytics'

type PanelProvider =
  | 'innovatemr'
  | 'provisionxn'
  | 'riwi'
  | 'clearinsights'
  | 'lucid'
  | 'mfour'
  | 'mindforce'
  | 'purespectrum'
  | 'repdata'
  | 'ROI'
  | 'symmetric'

export type RespondentID =
  | {
      respondentIncentive: number | null
      lucidRid: string
      lucidSurveyId: string
      Sid: 'lucid'
    }
  | {
      lucidRid: string
      Sid:
        | 'innovatemr'
        | 'provisionxn'
        | 'riwi'
        | 'clearinsights'
        | 'mfour'
        | 'mindforce'
        | 'purespectrum'
        | 'repdata'
        | 'ROI'
        | 'symmetric'
    }
  | { lucidRid: string }
  | undefined

const REQUIRED_QUERY_PARAMS = {
  innovatemr: ['RID'],
  provisionxn: ['RID'],
  riwi: ['RID'],
  clearinsights: ['RID'],
  lucid: ['lucidSurveyId', 'RID'],
  mfour: ['RID'],
  mindforce: ['RID'],
  purespectrum: ['ps_hash', 'RID'],
  repdata: ['RID'],
  ROI: ['RID'],
  symmetric: ['RID'],
} as const satisfies Record<PanelProvider, string[]>

const REDIRECT_URLS = {
  innovatemr: 'https://edgeapi.innovatemr.net/surveyDone',
  provisionxn: 'https://survey.hbgknowledge.com/survey/thankyou',
  riwi: 'https://sample-router.sayasdf.com/router/return/',
  clearinsights: 'https://gl0010126t.sawtoothsoftware.com/cgi-bin/ciwweb.pl',
  lucid: {
    COMPLETION: 'https://notch.insights.supply/cb',
    DISQUALIFICATION: 'https://samplicio.us/s/ClientCallBack.aspx',
  },
  mfour: 'https://www.surveysonthego.net/survey',
  mindforce: 'https://insightsenabled.mindforceresearch.in/postredirect',
  purespectrum: 'https://spectrumsurveys.com/surveydone',
  repdata: 'https://www.rdsecured.com/return',
  ROI: 'https://router.marketonce.com/vendor/surveyEnd',
  symmetric: 'https://icion.decisionanalyst.com/l/exit',
} as const satisfies Record<PanelProvider, string | Record<string, string>>

type InnovateMRTermCode = '1' | '2' | '3' | '4'
type ProvisionXNTermCode =
  | 'OURnUXFNeVc4RTE'
  | 'eWVZTkNGbmpJUjI'
  | 'R1k2dHFQTUI4bDM'
  | 'SHdpdXZBZEt0NDQ'
type RiwiTermCode = 'complete' | 'terminate' | 'quota' | 'quality'
type ClearInsightsTermCode = '1' | '2' | '3'
type LucidTermCode = '20' | '30' | '40'
type MfourTermCode = 'overquota' | 'qualityscreen' | 'terminate'
type MindforceTermCode = '20' | '30' | '40'
type PureSpectrumTermCode = '17' | '18' | '20'
type RepDataTermCode = '2000' | '3000' | '4000'
type RoiRocketTermCode = 'D' | 'R' | '75e889' | '8a9460'
type SymmetricTermCode =
  | 'Did Not Qualify'
  | 'OverQuota'
  | 'Quality'
  | 'Security'

type RedirectCodeForProvider<Provider extends PanelProvider> =
  Provider extends 'lucid'
    ? LucidTermCode
    : Provider extends 'innovatemr'
      ? InnovateMRTermCode
      : Provider extends 'provisionxn'
        ? ProvisionXNTermCode
        : Provider extends 'riwi'
          ? RiwiTermCode
          : Provider extends 'clearinsights'
            ? ClearInsightsTermCode
            : Provider extends 'mfour'
              ? MfourTermCode
              : Provider extends 'mindforce'
                ? MindforceTermCode
                : Provider extends 'purespectrum'
                  ? PureSpectrumTermCode
                  : Provider extends 'repdata'
                    ? RepDataTermCode
                    : Provider extends 'ROI'
                      ? RoiRocketTermCode
                      : Provider extends 'symmetric'
                        ? SymmetricTermCode
                        : never

/**
 * See mapping in our Notion documentation: https://www.notion.so/useglass/Disqualifications-99311333c6d94c48b048048693cbd527.
 */
const TACTIC_TO_REDIRECT_CODE = {
  innovatemr: {
    AUDIENCE_QUOTA: '3',
    BAD_RESPONDENT: '4',
    COPY_PASTE: '4',
    CUSTOM_QUALITY_SCREEN: '4',
    PRESURVEY_QUESTIONNAIRE: '4',
    QUESTION_QUOTA: '2',
    VARIABLE_QUOTA: '2',
    WAVE_QUOTA: '2',
  },
  provisionxn: {
    AUDIENCE_QUOTA: 'R1k2dHFQTUI4bDM',
    BAD_RESPONDENT: 'SHdpdXZBZEt0NDQ',
    COPY_PASTE: 'SHdpdXZBZEt0NDQ',
    CUSTOM_QUALITY_SCREEN: 'SHdpdXZBZEt0NDQ',
    PRESURVEY_QUESTIONNAIRE: 'SHdpdXZBZEt0NDQ',
    QUESTION_QUOTA: 'eWVZTkNGbmpJUjI',
    VARIABLE_QUOTA: 'eWVZTkNGbmpJUjI',
    WAVE_QUOTA: 'eWVZTkNGbmpJUjI',
  },
  riwi: {
    AUDIENCE_QUOTA: 'quota',
    BAD_RESPONDENT: 'quality',
    COPY_PASTE: 'quality',
    CUSTOM_QUALITY_SCREEN: 'quality',
    PRESURVEY_QUESTIONNAIRE: 'quality',
    QUESTION_QUOTA: 'terminate',
    VARIABLE_QUOTA: 'terminate',
    WAVE_QUOTA: 'terminate',
  },
  clearinsights: {
    AUDIENCE_QUOTA: '3',
    BAD_RESPONDENT: '2',
    COPY_PASTE: '2',
    CUSTOM_QUALITY_SCREEN: '2',
    PRESURVEY_QUESTIONNAIRE: '2',
    QUESTION_QUOTA: '3',
    VARIABLE_QUOTA: '3',
    WAVE_QUOTA: '3',
  },
  lucid: {
    AUDIENCE_QUOTA: '40',
    BAD_RESPONDENT: '30',
    COPY_PASTE: '30',
    CUSTOM_QUALITY_SCREEN: '30',
    PRESURVEY_QUESTIONNAIRE: '30',
    QUESTION_QUOTA: '20',
    VARIABLE_QUOTA: '20',
    WAVE_QUOTA: '40',
  },
  mfour: {
    AUDIENCE_QUOTA: 'overquota',
    BAD_RESPONDENT: 'qualityscreen',
    COPY_PASTE: 'qualityscreen',
    CUSTOM_QUALITY_SCREEN: 'qualityscreen',
    PRESURVEY_QUESTIONNAIRE: 'qualityscreen',
    QUESTION_QUOTA: 'terminate',
    VARIABLE_QUOTA: 'terminate',
    WAVE_QUOTA: 'overquota',
  },
  mindforce: {
    AUDIENCE_QUOTA: '40',
    BAD_RESPONDENT: '30',
    COPY_PASTE: '30',
    CUSTOM_QUALITY_SCREEN: '30',
    PRESURVEY_QUESTIONNAIRE: '30',
    QUESTION_QUOTA: '20',
    VARIABLE_QUOTA: '20',
    WAVE_QUOTA: '40',
  },
  purespectrum: {
    AUDIENCE_QUOTA: '17',
    BAD_RESPONDENT: '20',
    COPY_PASTE: '20',
    CUSTOM_QUALITY_SCREEN: '20',
    PRESURVEY_QUESTIONNAIRE: '20',
    QUESTION_QUOTA: '18',
    VARIABLE_QUOTA: '18',
    WAVE_QUOTA: '18',
  },
  repdata: {
    AUDIENCE_QUOTA: '2000',
    BAD_RESPONDENT: '3000',
    COPY_PASTE: '3000',
    CUSTOM_QUALITY_SCREEN: '3000',
    PRESURVEY_QUESTIONNAIRE: '3000',
    QUESTION_QUOTA: '2000',
    VARIABLE_QUOTA: '2000',
    WAVE_QUOTA: '4000',
  },
  ROI: {
    AUDIENCE_QUOTA: '75e889',
    BAD_RESPONDENT: 'R',
    COPY_PASTE: 'R',
    CUSTOM_QUALITY_SCREEN: 'R',
    PRESURVEY_QUESTIONNAIRE: 'R',
    QUESTION_QUOTA: '8a9460',
    VARIABLE_QUOTA: '8a9460',
    WAVE_QUOTA: '75e889',
  },
  symmetric: {
    AUDIENCE_QUOTA: 'OverQuota',
    BAD_RESPONDENT: 'Security',
    COPY_PASTE: 'Quality',
    CUSTOM_QUALITY_SCREEN: 'Quality',
    PRESURVEY_QUESTIONNAIRE: 'Quality',
    QUESTION_QUOTA: 'Did Not Qualify',
    VARIABLE_QUOTA: 'Did Not Qualify',
    WAVE_QUOTA: 'OverQuota',
  },
} as const satisfies Record<
  PanelProvider,
  Record<
    DisqualificationReason['tactic'],
    RedirectCodeForProvider<PanelProvider>
  >
>

export function areParametersValid({
  searchParams,
}: {
  searchParams: URLSearchParams
}) {
  const provider = searchParams.get('provider') as PanelProvider
  const requiredQueryParams = provider
    ? REQUIRED_QUERY_PARAMS[provider]
    : undefined

  return (
    requiredQueryParams?.every((param) => {
      return !!searchParams.get(param)
    }) ?? true
  )
}

export function getRespondentID({
  searchParams,
}: {
  searchParams: URLSearchParams
}): RespondentID {
  const provider = searchParams.get('provider') as PanelProvider
  const respondentID = searchParams.get('RID')

  if (provider === 'lucid') {
    return {
      respondentIncentive: toCents(searchParams.get('respondentIncentive')),
      lucidRid: respondentID ?? '',
      lucidSurveyId: searchParams.get('lucidSurveyId') ?? '',
      Sid: provider,
    }
  } else if (
    provider === 'innovatemr' ||
    provider === 'provisionxn' ||
    provider === 'riwi' ||
    provider === 'clearinsights' ||
    provider === 'mfour' ||
    provider === 'mindforce' ||
    provider === 'purespectrum' ||
    provider === 'repdata' ||
    provider === 'ROI' ||
    provider === 'symmetric'
  ) {
    return { lucidRid: respondentID ?? '', Sid: provider }
  } else if (respondentID) {
    return { lucidRid: respondentID }
  }
}

export function redirectCompleted({
  isTesting,
  searchParams,
}: {
  isTesting: boolean
  searchParams: URLSearchParams
}) {
  const provider = searchParams.get('provider') as PanelProvider
  const respondentID = searchParams.get('RID')

  if (respondentID) {
    if (provider === 'lucid') {
      return redirectCompletedLucid({ respondentID })
    } else if (provider === 'innovatemr') {
      return redirectCompletedInnovateMR({ respondentID })
    } else if (provider === 'provisionxn') {
      return redirectCompletedProvisionXN({ respondentID })
    } else if (provider === 'riwi') {
      return redirectCompletedRiwi({ respondentID })
    } else if (provider === 'clearinsights') {
      return redirectCompletedClearInsights({ respondentID })
    } else if (provider === 'mfour') {
      return redirectCompletedMfour({ respondentID })
    } else if (provider === 'mindforce') {
      return redirectCompletedMindforce({ respondentID })
    } else if (provider === 'purespectrum') {
      return redirectCompletedPureSpectrum({
        hash: searchParams.get('ps_hash') ?? '',
        respondentID,
      })
    } else if (provider === 'repdata') {
      return redirectCompletedRepData({ respondentID })
    } else if (provider === 'ROI') {
      return redirectCompletedROIRocket({ respondentID })
    } else if (provider === 'symmetric') {
      return redirectCompletedSymmetric({ respondentID })
    }
  }

  if (!isTesting) {
    track('Failed Redirect')
  }
}

function redirectCompletedInnovateMR({
  respondentID,
}: {
  respondentID: string
}) {
  const completionParams = new URLSearchParams([
    ['sc', '1'],
    ['ejid', 'Yj1ZKa2v'],
    ['tk', respondentID],
  ])

  window.location.href = `${REDIRECT_URLS.innovatemr}?${completionParams}`
}
function redirectCompletedProvisionXN({
  respondentID,
}: {
  respondentID: string
}) {
  const completionParams = new URLSearchParams([
    ['auth', 'OURnUXFNeVc4RTE'],
    ['uid', respondentID],
  ])

  window.location.href = `${REDIRECT_URLS.provisionxn}?${completionParams}`
}
function redirectCompletedRiwi({ respondentID }: { respondentID: string }) {
  const completionParams = new URLSearchParams([['rid', respondentID]])

  window.location.href = `${REDIRECT_URLS.riwi}complete?${completionParams}`
}

function redirectCompletedClearInsights({
  respondentID,
}: {
  respondentID: string
}) {
  const completionParams = new URLSearchParams([
    ['studyname', 'gl0010126tredirects'],
    ['result', '1'],
    ['ID', respondentID],
  ])

  window.location.href = `${REDIRECT_URLS.clearinsights}?${completionParams}`
}

function redirectCompletedLucid({ respondentID }: { respondentID: string }) {
  const completionParams = new URLSearchParams([
    ['token', 'a29ae3ef-4439-49d7-89df-9d9dacb63878'],
    ['RID', respondentID],
  ])

  window.location.href = `${REDIRECT_URLS.lucid.COMPLETION}?${completionParams}`
}

function redirectCompletedMfour({ respondentID }: { respondentID: string }) {
  const completionParams = new URLSearchParams([['ID', respondentID]])

  window.location.href = `${REDIRECT_URLS.mfour}/complete?${completionParams}`
}

function redirectCompletedMindforce({
  respondentID,
}: {
  respondentID: string
}) {
  const completionParams = new URLSearchParams([
    ['identifier', respondentID],
    ['status', '10'],
  ])

  window.location.href = `${REDIRECT_URLS.mindforce}?${completionParams}`
}

function redirectCompletedPureSpectrum({
  hash,
  respondentID,
}: {
  hash: string
  respondentID: string
}) {
  const completionParams = new URLSearchParams([
    ['transaction_id', respondentID],
    ['st', '21'],
    ['ps_hash', hash],
  ])

  window.location.href = `${REDIRECT_URLS.purespectrum}?${completionParams}`
}

function redirectCompletedRepData({ respondentID }: { respondentID: string }) {
  const completionParams = new URLSearchParams([
    ['rid', respondentID],
    ['inbound_code', '1000'],
  ])

  window.location.href = `${REDIRECT_URLS.repdata}?${completionParams}`
}

function redirectCompletedROIRocket({
  respondentID,
}: {
  respondentID: string
}) {
  const completionParams = new URLSearchParams([
    ['vid', respondentID],
    ['ss', 'a90e22'],
  ])

  window.location.href = `${REDIRECT_URLS.ROI}?${completionParams}`
}

function redirectCompletedSymmetric({
  respondentID,
}: {
  respondentID: string
}) {
  const completionParams = new URLSearchParams([
    ['MID', respondentID],
    ['status', 'Complete'],
  ])

  window.location.href = `${REDIRECT_URLS.symmetric}?${completionParams}`
}

export function redirectDisqualified({
  disqualificationReason,
  isTesting,
  searchParams,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  isTesting: boolean
  searchParams: URLSearchParams
}) {
  const provider = searchParams.get('provider') as PanelProvider
  const respondentID = searchParams.get('RID')

  if (respondentID) {
    const opts = { disqualificationReason, respondentID }

    if (provider === 'lucid') {
      return redirectDisqualifiedLucid(opts)
    } else if (provider === 'innovatemr') {
      return redirectDisqualifiedInnovateMR(opts)
    } else if (provider === 'provisionxn') {
      return redirectDisqualifiedProvisionXN(opts)
    } else if (provider === 'riwi') {
      return redirectDisqualifiedRiwi(opts)
    } else if (provider === 'clearinsights') {
      return redirectDisqualifiedClearInsights(opts)
    } else if (provider === 'mfour') {
      return redirectDisqualifiedMfour(opts)
    } else if (provider === 'mindforce') {
      return redirectDisqualifiedMindforce(opts)
    } else if (provider === 'purespectrum') {
      return redirectDisqualifiedPureSpectrum(opts)
    } else if (provider === 'repdata') {
      return redirectDisqualifiedRepData(opts)
    } else if (provider === 'ROI') {
      return redirectDisqualifiedROIRocket(opts)
    } else if (provider === 'symmetric') {
      return redirectDisqualifiedSymmetric(opts)
    }
  }

  if (!isTesting) {
    track('Failed Redirect')
  }
}

function redirectDisqualifiedInnovateMR({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic ? TACTIC_TO_REDIRECT_CODE.innovatemr[tactic] : '2'
  const disqualParams = new URLSearchParams([
    ['sc', redirectCode],
    ['tk', respondentID],
  ])

  window.location.href = `${REDIRECT_URLS.innovatemr}?${disqualParams}`
}

function redirectDisqualifiedProvisionXN({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic
    ? TACTIC_TO_REDIRECT_CODE.provisionxn[tactic]
    : 'eWVZTkNGbmpJUjI'
  const disqualParams = new URLSearchParams([
    ['auth', redirectCode],
    ['uid', respondentID],
  ])

  window.location.href = `${REDIRECT_URLS.provisionxn}?${disqualParams}`
}

function redirectDisqualifiedRiwi({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic
    ? TACTIC_TO_REDIRECT_CODE.riwi[tactic]
    : 'terminate'
  const disqualParams = new URLSearchParams([['rid', respondentID]])

  window.location.href = `${REDIRECT_URLS.riwi}exit/${redirectCode}/?${disqualParams}`
}

function redirectDisqualifiedClearInsights({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic
    ? TACTIC_TO_REDIRECT_CODE.clearinsights[tactic]
    : '2'
  const disqualParams = new URLSearchParams([
    ['studyname', 'gl0010126tredirects'],
    ['result', redirectCode],
    ['ID', respondentID],
  ])

  window.location.href = `${REDIRECT_URLS.clearinsights}?${disqualParams}`
}

function redirectDisqualifiedLucid({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic ? TACTIC_TO_REDIRECT_CODE.lucid[tactic] : '20'
  const disqualParams = new URLSearchParams([
    ['RID', respondentID],
    ['RIS', redirectCode],
  ])

  window.location.href = `${REDIRECT_URLS.lucid.DISQUALIFICATION}?${disqualParams}`
}

function redirectDisqualifiedMfour({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic
    ? TACTIC_TO_REDIRECT_CODE.mfour[tactic]
    : 'terminate'
  const disqualParams = new URLSearchParams([['ID', respondentID]])

  window.location.href = `${REDIRECT_URLS.mfour}/${redirectCode}?${disqualParams}`
}

function redirectDisqualifiedMindforce({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic ? TACTIC_TO_REDIRECT_CODE.mindforce[tactic] : '20'
  const disqualParams = new URLSearchParams([
    ['identifier', respondentID],
    ['status', redirectCode],
  ])

  window.location.href = `${REDIRECT_URLS.mindforce}?${disqualParams}`
}

function redirectDisqualifiedPureSpectrum({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic
    ? TACTIC_TO_REDIRECT_CODE.purespectrum[tactic]
    : '18'
  const disqualParams = new URLSearchParams([
    ['transaction_id', respondentID],
    ['st', redirectCode],
  ])

  window.location.href = `${REDIRECT_URLS.purespectrum}?${disqualParams}`
}

function redirectDisqualifiedRepData({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic ? TACTIC_TO_REDIRECT_CODE.repdata[tactic] : '2000'
  const disqualParams = new URLSearchParams([
    ['rid', respondentID],
    ['inbound_code', redirectCode],
  ])

  window.location.href = `${REDIRECT_URLS.repdata}?${disqualParams}`
}

function redirectDisqualifiedROIRocket({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic ? TACTIC_TO_REDIRECT_CODE.ROI[tactic] : 'T'
  const disqualParams = new URLSearchParams([
    ['vid', respondentID],
    ['ss', redirectCode],
  ])

  window.location.href = `${REDIRECT_URLS.ROI}?${disqualParams}`
}

function redirectDisqualifiedSymmetric({
  disqualificationReason,
  respondentID,
}: {
  disqualificationReason?: { tactic: DisqualificationReason['tactic'] }
  respondentID: string
}) {
  const { tactic } = disqualificationReason || {}
  const redirectCode = tactic
    ? TACTIC_TO_REDIRECT_CODE.symmetric[tactic]
    : 'Did Not Qualify'
  const disqualParams = new URLSearchParams([
    ['MID', respondentID],
    ['status', redirectCode],
  ])

  window.location.href = `${REDIRECT_URLS.symmetric}?${disqualParams}`
}
